/**
 * Used-vehicle listings ("használt autók").
 *
 * The workshop sells a small, mostly-electric used stock alongside the service
 * business. Unlike the job form, nothing here is a port of an Angular contract
 * — these shapes are new, so they are typed properly from the start: enums
 * rather than loose strings, `null` rather than `''` for "not known", and
 * separate summary/detail shapes so the listing payload doesn't carry every
 * spec of every car.
 *
 * Enum *values* are the strings stored in Postgres and sent over the wire;
 * their human-readable labels live in the frontends' i18n message files
 * (`vehicles.enum.*`), which is what makes the whole spec table bilingual
 * without storing a second copy of every car.
 */

export enum VehicleStatus {
  /** Being prepared; invisible to the public site. */
  DRAFT = 'draft',
  /** Listed and for sale. */
  ACTIVE = 'active',
  /** Deal in progress — still listed, badged "Lefoglalva", form still open. */
  RESERVED = 'reserved',
  /**
   * Gone. Dropped from the listing, but the detail URL stays alive showing
   * "Elkelt" so a link already shared or indexed never 404s.
   */
  SOLD = 'sold',
}

export enum FuelType {
  ELECTRIC = 'electric',
  PLUGIN_HYBRID = 'plugin_hybrid',
  HYBRID = 'hybrid',
  PETROL = 'petrol',
  DIESEL = 'diesel',
  LPG = 'lpg',
}

/** The fuel types for which the EV spec block (battery, range, charging) applies. */
export const ELECTRIFIED_FUEL_TYPES: readonly FuelType[] = [
  FuelType.ELECTRIC,
  FuelType.PLUGIN_HYBRID,
];

export enum Transmission {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
}

export enum Drivetrain {
  FWD = 'fwd',
  RWD = 'rwd',
  AWD = 'awd',
}

export enum BodyType {
  HATCHBACK = 'hatchback',
  SEDAN = 'sedan',
  WAGON = 'wagon',
  SUV = 'suv',
  MPV = 'mpv',
  COUPE = 'coupe',
  CABRIO = 'cabrio',
  VAN = 'van',
  PICKUP = 'pickup',
}

/** Sort orders the listing offers. The API applies these, not the client. */
export enum VehicleSort {
  NEWEST = 'newest',
  PRICE_ASC = 'price-asc',
  PRICE_DESC = 'price-desc',
  YEAR_DESC = 'year-desc',
  MILEAGE_ASC = 'mileage-asc',
}

export const VEHICLE_SORTS: readonly VehicleSort[] = Object.values(VehicleSort);

export interface VehicleImageRef {
  id: number;
  /** Storage key. Useful to the admin; the public site only needs `url`. */
  filename: string;
  /**
   * Where to actually load the image from — a disk path or a Supabase Storage
   * CDN URL, depending on how the API is configured. Never build this on the
   * frontend.
   */
  url: string;
  /** Sort order. 0 is the cover image shown on the listing card. */
  priority: number;
  /** Alt text, required at upload time for accessibility. */
  alt: string;
}

/**
 * The fields a listing card needs — deliberately a subset, so the listing
 * endpoint doesn't ship fifteen full spec sheets to render twelve cards.
 */
export interface PublicVehicleSummary {
  id: number;
  slug: string;
  make: string;
  model: string;
  variant: string | null;
  year: number;
  mileageKm: number;
  /** `null` means "ask us" — rendered as "Ár kérésre", never as 0 Ft. */
  priceHuf: number | null;
  fuelType: FuelType;
  transmission: Transmission;
  powerKw: number | null;
  batteryCapacityKwh: number | null;
  rangeKm: number | null;
  status: VehicleStatus;
  /** The cover image, or `null` for a car with no photos yet. */
  coverImage: VehicleImageRef | null;
}

/** Everything the detail page shows. */
export interface PublicVehicle extends PublicVehicleSummary {
  bodyType: BodyType | null;
  drivetrain: Drivetrain | null;
  powerHp: number | null;
  colour: string | null;
  doors: number | null;
  seats: number | null;
  /** ISO date (YYYY-MM-DD) of first registration. */
  firstRegistration: string | null;
  /** ISO date (YYYY-MM-DD) the MOT ("műszaki vizsga") runs out. */
  motExpiry: string | null;
  /** True when the price is invoiced with reclaimable VAT ("ÁFÁ-s"). */
  vatDeductible: boolean;
  batteryHealthPercent: number | null;
  chargingAcKw: number | null;
  chargingDcKw: number | null;
  /** Free text, e.g. "Type 2 + Schuko". */
  cableSet: string | null;
  /** Bullet list of equipment, as entered in the admin. */
  equipment: string[];
  /** Hungarian description. Always present. */
  descriptionHu: string;
  /**
   * English description. Optional by design: requiring it would double data
   * entry for every car and the second language would quietly stop being
   * filled. The public site falls back to `descriptionHu` when this is empty.
   */
  descriptionEn: string | null;
  images: VehicleImageRef[];
}

/** A vehicle as the admin list shows it. */
export interface AdminVehicle extends PublicVehicle {
  createdAt: string;
  updatedAt: string;
  imageCount: number;
}

/**
 * Create/update payload. Images are managed through their own endpoints
 * rather than this body — a 20-photo car would otherwise re-upload every
 * photo on every price edit.
 */
export interface VehicleUpsertRequest {
  make: string;
  model: string;
  variant?: string | null;
  year: number;
  mileageKm: number;
  priceHuf?: number | null;
  vatDeductible?: boolean;
  fuelType: FuelType;
  transmission: Transmission;
  bodyType?: BodyType | null;
  drivetrain?: Drivetrain | null;
  powerKw?: number | null;
  powerHp?: number | null;
  colour?: string | null;
  doors?: number | null;
  seats?: number | null;
  firstRegistration?: string | null;
  motExpiry?: string | null;
  batteryCapacityKwh?: number | null;
  rangeKm?: number | null;
  batteryHealthPercent?: number | null;
  chargingAcKw?: number | null;
  chargingDcKw?: number | null;
  cableSet?: string | null;
  equipment?: string[];
  descriptionHu: string;
  descriptionEn?: string | null;
  status: VehicleStatus;
}

/** One photo being attached to a vehicle. */
export interface AddVehicleImageRequest {
  /** Original filename; the API derives a collision-free storage key from it. */
  name: string;
  /** Raw base64, without the `data:image/...;base64,` prefix. */
  base64: string;
  alt: string;
}

/** "Érdeklődöm" — a buyer's enquiry about one specific car. */
export interface VehicleInquiryRequest {
  name: string;
  email: string;
  phone: string;
  message: string;
  /** The privacy notice must be accepted, same as the job form. */
  privacy: boolean;
}

/** Caps the API enforces on an inquiry, mirrored by the form's validation. */
export const INQUIRY_LIMITS = {
  name: 120,
  email: 160,
  phone: 40,
  message: 2000,
} as const;
